import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Avatar } from '@/components/Avatar';
import { MenuIcon } from '@/components/MenuIcon';
import { Sidebar } from '@/components/Sidebar';
import { logout, useSession, type VendorKind } from '@/lib/auth/session';
import { IN_SAFE_APP, isSafeAppPath, SAFE_APP_TITLE } from '@/lib/safeApp';
import { useFieldGrants } from '@/lib/field';
import { useSelectedSite } from '@/lib/useSelectedSite';

const SIDEBAR_WIDTH = 208; // front 의 lg:pl-52 와 같은 값

/**
 * 앱 껍데기 — 좌측 메뉴 · 머리줄 · 본문.
 *
 * <h2>사이드바가 자리를 차지하는 지점은 lg(1024) 다</h2>
 * front 와 같은 값이다. 그보다 좁으면 서랍으로 물러난다 — 아이패드 세로(768)에서 사이드바가
 * 자리를 차지한 채 본문을 밀면 위젯 한 칸이 눌려 글자가 서너 자만 남는다.
 *
 * <h2>현장 선택은 머리줄에 있다</h2>
 * 사이드바 메뉴가 전부 `/sites/:siteId/…` 를 가리키므로 현장은 <b>앱 전체가 공유하는 하나</b>여야
 * 한다({@link useSelectedSite}). 화면마다 따로 고르게 두면 기성 화면은 A 현장, 출역 화면은 B 현장을
 * 보는 상태가 만들어진다.
 */
/**
 * 세션이 아직일 때 넘기는 <b>고정된</b> 빈 목록.
 *
 * <p>`?? []` 를 그 자리에 적으면 매 렌더 새 배열이라 사이드바의 메뉴 계산이 통째로 다시 돈다 —
 * 값은 같은데 참조만 바뀌는 자리가 memo 를 무력화한다.
 */
const NO_KINDS: VendorKind[] = [];

export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { sites, current, partyType, choose } = useSelectedSite();
  // 직원의 현장 입력 메뉴는 웹 메뉴 조회 권한으로 선다. `can` 은 응답이 바뀔 때만 새 함수다.
  const { can } = useFieldGrants();
  const canViewWebMenu = useCallback((webMenu: string) => can(webMenu, 'view'), [can]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // 화면을 옮기면 서랍은 닫힌다. 열어 둔 채로 두면 새 화면 위에 겹쳐 남는다.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  async function onLogout() {
    await logout();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  /** 현장을 바꾸면 같은 메뉴의 그 현장 화면으로 옮긴다 — 보던 자리를 잃지 않는다. */
  function onChangeSite(nextId: string) {
    choose(nextId);
    const match = /^\/sites\/[^/]+\/(.+)$/.exec(location.pathname);
    navigate(match !== null ? `/sites/${nextId}/${match[1]}` : '/', { replace: true });
  }

  const party = session.data;

  /*
    홈(위젯 격자)은 **전폭**이다. front 의 AppLayout 과 같다 — 본문을 4xl(896px) 로 묶어 두면 넓은 화면에서
    메이슨리가 2열에서 멈추고 양옆이 빈 채로 남는다(2026-09-11 실측 · 1,913px 에서 절반이 여백).
    목록·폼 화면은 그대로 4xl 이다: 한 줄이 너무 길면 손에 든 화면에서 눈이 줄을 잃는다.
  */
  const home = location.pathname === '/' || location.pathname === '/home';

  return (
    <div className="min-h-screen">
      {/* ── 머리줄 ─────────────────────────────────────────────────────── */}
      <header
        className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-2 border-b px-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          className="rounded-md p-2 lg:hidden"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="메뉴"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon name="grid" size={18} />
        </button>

        {/*
          소속 이름 자리에 얼굴을 함께 둔다(사용자 지시 2026-09-30). 누르면 마이페이지다 —
          자기 얼굴을 누르는 것이 「내 정보」로 가는 가장 짧은 길이고, 메뉴 줄을 늘리지 않는다.
          사진이 없으면 아바타가 이름 첫 글자로 선다.
        */}
        <Link to="/mypage" className="flex min-w-0 items-center gap-2 lg:w-52 lg:shrink-0" aria-label="마이페이지">
          <Avatar size={30} name={party?.name} />
          <span className="min-w-0">
            <span className="block truncate text-ui-md font-semibold">{party?.partyName ?? (IN_SAFE_APP ? SAFE_APP_TITLE : 'BoMI Partners')}</span>
            {party !== undefined && (
              <span className="block truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                {party.name}
              </span>
            )}
          </span>
        </Link>

        {sites.length > 1 && current !== undefined && (
          <select
            className="input-field ml-auto min-h-0 max-w-[45%] py-1.5 text-ui-xs lg:ml-0 lg:max-w-64"
            value={current.id}
            onChange={(event) => onChangeSite(event.target.value)}
            aria-label="현장 선택"
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        )}

        {/* 마이페이지는 로그아웃 옆이다(사용자 지시 2026-09-30). 아바타와 같은 곳으로 간다. */}
        <Link
          to="/mypage"
          className="btn-secondary ml-auto shrink-0 px-3 py-1.5 text-ui-xs"
        >
          마이페이지
        </Link>
        <button
          type="button"
          className="btn-secondary shrink-0 px-3 py-1.5 text-ui-xs"
          onClick={onLogout}
        >
          로그아웃
        </button>
      </header>

      {/* ── 좌측 메뉴 (lg 이상 고정) ────────────────────────────────────── */}
      {/*
        스크롤은 여기가 아니라 안쪽 메뉴 목록이 한다({@link Sidebar}) — 여기서 넘치게 두면
        바닥에 붙인 「위젯 관리」가 메뉴를 따라 화면 밖으로 밀려 올라간다.
      */}
      <aside
        className="fixed bottom-0 left-0 top-14 z-20 hidden overflow-hidden border-r lg:block"
        style={{ width: SIDEBAR_WIDTH, background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <Sidebar siteId={current?.id} partyType={partyType} kinds={session.data?.vendorKinds ?? NO_KINDS} canViewWebMenu={canViewWebMenu} />
      </aside>

      {/* ── 좌측 메뉴 (lg 미만 서랍) ────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="메뉴 닫기"
            className="absolute inset-0"
            style={{ background: 'rgba(15, 23, 42, 0.4)' }}
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute bottom-0 left-0 top-0 overflow-hidden border-r"
            style={{ width: SIDEBAR_WIDTH, background: 'var(--bg-card)', borderColor: 'var(--border)' }}
          >
            <Sidebar siteId={current?.id} partyType={partyType} kinds={session.data?.vendorKinds ?? NO_KINDS} canViewWebMenu={canViewWebMenu} onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <main className="px-4 pb-8 pt-16 lg:pl-[calc(208px+1rem)]">
        <div className={home ? 'w-full' : 'mx-auto w-full max-w-4xl'}>
          {/*
            안전관리 앱에서는 안전 밖의 주소(알림·공유 링크로 들어온 검측·일보 …)를 홈(= 안전)으로 돌린다 — 메뉴에서
            걷은 화면이 주소로 열리면 「안전만 보이게」(사용자 결정 2026-10-29)가 그 한 번에 깨진다.
          */}
          {IN_SAFE_APP && !isSafeAppPath(location.pathname) ? <Navigate to="/" replace /> : <Outlet />}
        </div>
      </main>
    </div>
  );
}
